import React, { useEffect, useRef, useState } from "react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { ChevronDownIcon, RotateCw, BarChart2, Download,PieChart } from "lucide-react";
import {
  getTokenUsageForPaginatedUsers,
  getTokenUsagePresignedUrl,
} from "../../../../service/apiService";
import { TokenUsageCoverage } from "../../../../types";
import MonthlyTokenUsageCharts, { MonthlyTokenUsageChartsHandle } from "./MonthlyTokenUsageCharts";
import TokenUsageCoveragePanel from "./TokenUsageCoverage";

interface MonthlyTokenUsage {
  total_input_tokens: string;
  total_output_tokens: string;
  total_tokens: string;
  files_uploaded: number;
  brainbank_files_uploaded: number;
  sessions_created: number;
  total_conversation_time_seconds: number;
  vas_used: string[];
  month: string;
}

interface UserMonthlyTokenData {
  email: string;
  usage: MonthlyTokenUsage;
}

interface PaginationData {
  page: number;
  items_per_page: number;
  total_pages: number;
  has_next: boolean;
  next_page: number | null;
}

interface TotalUsageMetadata {
  total_usage: {
    total_input_tokens: number;
    total_output_tokens: number;
    total_tokens: number;
    files_uploaded: number;
    brainbank_files_uploaded: number;
    sessions_created: number;
    total_conversation_time_seconds: number;
    month: string;
  };
}

const formatDuration = (seconds: number): string => {
  if (!seconds || seconds <= 0) return "0m";
  if (seconds < 60) return "<1m";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours && minutes) return `${hours}h ${minutes}m`;
  if (hours) return `${hours}h`;
  return `${minutes}m`;
};

const MonthlyTokenUsageTable = () => {
  const [userData, setUserData] = useState<UserMonthlyTokenData[]>([]);
  const [metadata, setMetadata] = useState<TotalUsageMetadata | null>(null);
  // AWS's own metered totals for the month. Shown as the headline figure because
  // it is the number the invoice is built from; what we record per user is only
  // ever a subset of it (see the coverage line under each tile).
  const [coverage, setCoverage] = useState<TokenUsageCoverage | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    items_per_page: 10,
    total_pages: 1,
    has_next: false,
    next_page: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'chart'>('table');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const chartsRef = useRef<MonthlyTokenUsageChartsHandle>(null);

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0"
    )}`;
  });

  // The month the tables are actually showing. Kept apart from `selectedMonth`
  // so moving the month picker doesn't refetch until Submit is pressed - the
  // existing behaviour for the table, now shared with the coverage panel.
  const [appliedMonth, setAppliedMonth] = useState(selectedMonth);

  const fetchTokenUsage = async (page: number, itemsPerPage: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const timePeriod = "monthly";
      const result = await getTokenUsageForPaginatedUsers(
        page,
        itemsPerPage,
        timePeriod,
        selectedMonth
      );

      const formattedUserData: UserMonthlyTokenData[] = Object.entries(
        result.usage_data
      ).map(([email, usage]) => ({
        email,
        usage: usage as MonthlyTokenUsage,
      }));

      setUserData(formattedUserData);
      setPagination(result.pagination);
      setMetadata(result.metadata);
    } catch (error) {
      console.error("Error fetching monthly token usage:", error);
      setError(
        "Failed to fetch monthly token usage data. Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTokenUsage(pagination.page, pagination.items_per_page);
  }, [pagination.page, pagination.items_per_page]);


  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleMonthSubmit = () => {
    setAppliedMonth(selectedMonth);
    // Avoid a double-fetch: when page > 1, changing it triggers the useEffect
    // refetch on its own; only fetch directly when page is already 1.
    if (pagination.page === 1) {
      fetchTokenUsage(1, pagination.items_per_page);
    } else {
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  };

  const formatNumber = (num: number) => num.toLocaleString();

  // True only once the CloudWatch sync job has written a row for this month.
  // Until then there is no AWS figure to show and the tiles stay on the
  // attributed number rather than displaying a misleading zero.
  const accountAvailable = Boolean(
    coverage?.account?.available && coverage.account.total_tokens > 0
  );

  /**
   * One token tile: AWS's metered figure as the headline, with the portion we
   * could attribute to a user underneath.
   *
   * The two are deliberately not summed and not swapped. The AWS number is what
   * the invoice charges; the attributed number is what our own instrumentation
   * caught. Showing only the latter is what made this summary read ~20x low for
   * August 2026 -- every call path we had not instrumented was simply invisible.
   */
  const renderTokenTile = (
    label: string,
    accountValue: number,
    attributedValue: number
  ) => {
    const headline = accountAvailable ? accountValue : attributedValue;
    const percent =
      accountAvailable && accountValue > 0
        ? (attributedValue / accountValue) * 100
        : null;

    return (
      <div className="flex h-full min-w-0 flex-col justify-between rounded-lg bg-violet-50 p-4">
        <p className="text-sm font-medium leading-snug text-violet-600">
          {label}
          {accountAvailable && (
            <span className="ml-1 font-normal text-violet-500">(AWS actual)</span>
          )}
        </p>
        <p className="mt-2 whitespace-nowrap text-lg font-bold tabular-nums text-violet-700 xl:text-xl">
          {formatNumber(headline)}
        </p>
        {accountAvailable && (
          <p className="mt-1 text-xs leading-snug text-violet-500">
            Attributed to users: {formatNumber(attributedValue)}
            {percent !== null && ` (${percent.toFixed(1)}%)`}
          </p>
        )}
      </div>
    );
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      if (viewMode === 'chart') {
        await chartsRef.current?.downloadAllAsZip(selectedMonth);
        return;
      }
      const response = await getTokenUsagePresignedUrl(
        selectedMonth,
        Array.from(selectedUserIds)
      );
      const link = document.createElement("a");
      link.href = response;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting token usage:", error);
      setError("Failed to export token usage data. Please try again later.");
    } finally {
      setIsExporting(false);
    }
  };

  const toggleUser = (email: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const allOnPageSelected =
    userData.length > 0 && userData.every((u) => selectedUserIds.has(u.email));

  const togglePageSelection = () => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        userData.forEach((u) => next.delete(u.email));
      } else {
        userData.forEach((u) => next.add(u.email));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedUserIds(new Set());

  // Function to display the selected month correctly across time zones
  const getFormattedMonthDisplay = (yearMonthString: string) => {
    // Parse the year and month from the YYYY-MM format
    const [year, month] = yearMonthString.split('-').map(Number);
    
    // Create a date object using UTC to avoid timezone issues
    // Note: month is 0-indexed in JavaScript Date
    const date = new Date(Date.UTC(year, month - 1, 1));
    
    // Format the date to show month and year only
    return date.toLocaleDateString("default", {
      month: "long",
      year: "numeric",
      timeZone: "UTC" // This ensures consistent display across time zones
    });
  };

  return (
    <Disclosure as="div" className="py-2">
      <DisclosureButton className="group flex w-full items-center justify-between">
        <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
          Token Usage Report (monthly)
        </span>
        <ChevronDownIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-300 group-open:rotate-180" />
      </DisclosureButton>
      <DisclosurePanel className="mt-4 text-sm text-gray-600">
        <div className="mb-4 flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-4">
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-full md:w-auto rounded-md border border-gray-300 py-2 px-4 text-sm font-medium text-gray-900 bg-white hover:bg-gray-50 shadow-sm focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500 transition-colors duration-200"
          />
          <button
            onClick={handleMonthSubmit}
            className="w-full md:w-auto inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
          >
            Submit
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="w-full md:w-auto inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:bg-violet-400 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <>
                <RotateCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="-ml-1 mr-2 h-4 w-4" />
                Export
              </>
            )}
          </button>
          {/* View toggle buttons */}
<div className="w-full md:w-auto md:ml-auto flex rounded-md shadow-sm">
  <button
    type="button"
    onClick={() => setViewMode('table')}
    className={`flex items-center px-4 py-2 text-sm font-medium rounded-l-md border ${
      viewMode === 'table' 
        ? 'bg-violet-600 text-white border-violet-600' 
        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
    }`}
  >
    <BarChart2 className="-ml-1 mr-2 h-4 w-4" />
    Table
  </button>
  <button
    type="button"
    onClick={() => setViewMode('chart')}
    className={`flex items-center px-4 py-2 text-sm font-medium rounded-r-md border ${
      viewMode === 'chart' 
        ? 'bg-violet-600 text-white border-violet-600' 
        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
    }`}
  >
    <PieChart className="-ml-1 mr-2 h-4 w-4" />
    Charts
  </button>
</div>
        </div>

        {/* Attributed vs account-wide Bedrock tokens for the month */}
        <TokenUsageCoveragePanel month={appliedMonth} onLoaded={setCoverage} />

        {/* Total Usage Summary */}
        {metadata && (
          <div className="mb-6 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart2 className="w-5 h-5 text-violet-600" />
                  <h3 className="text-base font-medium text-gray-900">
                    Total Usage Summary
                  </h3>
                </div>
                <div className="text-sm text-gray-600">
                  {getFormattedMonthDisplay(selectedMonth)}
                </div>
              </div>
            </div>
                        <div className="px-6 py-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {renderTokenTile(
                  "Total Input Tokens",
                  coverage?.account?.input_tokens || 0,
                  parseInt(metadata.total_usage.total_input_tokens.toString())
                )}
                {renderTokenTile(
                  "Total Output Tokens",
                  coverage?.account?.output_tokens || 0,
                  parseInt(metadata.total_usage.total_output_tokens.toString())
                )}
                <div className="flex h-full min-w-0 flex-col justify-between rounded-lg bg-violet-50 p-4">
                  <p className="text-sm font-medium leading-snug text-violet-600">
                    Total Files Uploaded in Session
                  </p>
                  <p className="mt-2 whitespace-nowrap text-lg font-bold tabular-nums text-violet-700 xl:text-xl">
                    {formatNumber(metadata.total_usage.files_uploaded || 0)}
                  </p>
                </div>
                <div className="flex h-full min-w-0 flex-col justify-between rounded-lg bg-violet-50 p-4">
                  <p className="text-sm font-medium leading-snug text-violet-600">
                    Total Files Uploaded in BrainBanks
                  </p>
                 <p className="mt-2 whitespace-nowrap text-lg font-bold tabular-nums text-violet-700 xl:text-xl">
                   {formatNumber(metadata.total_usage.brainbank_files_uploaded || 0)}
                 </p>
               </div>
               <div className="flex h-full min-w-0 flex-col justify-between rounded-lg bg-violet-50 p-4">
                 <p className="text-sm font-medium leading-snug text-violet-600">
                   Total Unique Conversations
                 </p>
                <p className="mt-2 whitespace-nowrap text-lg font-bold tabular-nums text-violet-700 xl:text-xl">
                  {formatNumber(metadata.total_usage.sessions_created || 0)}
                </p>
               </div>
               <div className="flex h-full min-w-0 flex-col justify-between rounded-lg bg-violet-50 p-4">
                 <p className="text-sm font-medium leading-snug text-violet-600">
                   Total Conversation Time
                 </p>
                <p className="mt-2 whitespace-nowrap text-lg font-bold tabular-nums text-violet-700 xl:text-xl">
                  {formatDuration(metadata.total_usage.total_conversation_time_seconds || 0)}
                </p>
               </div>
              </div>
            </div>
          </div>
        )}

        {/* Conditional rendering based on view mode */}
{viewMode === 'chart' ? (
  // Charts view
  <div className="mt-6">
    <MonthlyTokenUsageCharts ref={chartsRef} />
  </div>
) : (
  // Table view (original content)
  <>
    {selectedUserIds.size > 0 && (
      <div className="mb-3 flex items-center justify-between rounded-md bg-violet-50 px-4 py-2 text-sm text-violet-700">
        <span>
          {selectedUserIds.size} user{selectedUserIds.size === 1 ? "" : "s"} selected for export
        </span>
        <button
          onClick={clearSelection}
          className="text-violet-700 hover:text-violet-900 underline text-xs font-medium"
        >
          Clear selection
        </button>
      </div>
    )}
    {pagination && (
      <div className="mb-4 flex justify-between items-center">
        <button
          onClick={() => handlePageChange(pagination.page - 1)}
          disabled={pagination.page === 1 || isLoading}
          className={`inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
            pagination.page === 1 || isLoading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-violet-600 hover:bg-violet-700"
          } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500`}
        >
          Previous
        </button>
        <span>
          Page {pagination.page} of {pagination.total_pages}
        </span>
        <button
          onClick={() => handlePageChange(pagination.page + 1)}
          disabled={!pagination.has_next || isLoading}
          className={`inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
            !pagination.has_next || isLoading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-violet-600 hover:bg-violet-700"
          } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500`}
        >
          Next
        </button>
      </div>
    )}

    <div className="mt-6 overflow-auto rounded-lg border border-gray-200 max-h-[60vh]">
      {isLoading ? (
        <div className="flex items-center space-x-2">
          <RotateCw className="animate-spin h-5 w-5 text-gray-500" />
          <span>Loading...</span>
        </div>
      ) : error ? (
        <div>Error: {error}</div>
      ) : (
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100 border-b border-gray-200 sticky top-0 z-20">
            <tr>
              <th className="sticky left-0 z-30 w-12 bg-gray-100 px-4 py-3 text-left">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                  checked={allOnPageSelected}
                  onChange={togglePageSelection}
                  aria-label="Select all users on this page"
                />
              </th>
              <th className="sticky left-12 z-30 bg-gray-100 border-r border-gray-200 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Input
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Output
              </th>
               <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Files in Session
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Files in BrainBanks
             </th>
             <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Unique Conversations
             </th>
             <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Conv. Time
             </th>
             <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                VAs Used
             </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {userData.length > 0 ? (
              userData.map((user) => (
                <tr key={user.email}>
                  <td className="sticky left-0 z-10 w-12 bg-white px-4 py-4">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                      checked={selectedUserIds.has(user.email)}
                      onChange={() => toggleUser(user.email)}
                      aria-label={`Select ${user.email}`}
                    />
                  </td>
                  <td className="sticky left-12 z-10 bg-white border-r border-gray-200 px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.email}
                  </td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-900">
                    {formatNumber(parseInt(user.usage.total_input_tokens))}
                  </td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-900">
                    {formatNumber(parseInt(user.usage.total_output_tokens))}
                  </td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-900">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
                      {user.usage.files_uploaded || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-900">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
                      {user.usage.brainbank_files_uploaded || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-900">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
                      {user.usage.sessions_created || 0}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center whitespace-nowrap text-sm text-gray-900">
                    {formatDuration(user.usage.total_conversation_time_seconds || 0)}
                  </td>
                  <td className="px-6 py-4 text-center text-sm text-gray-900">
                    {user.usage.vas_used && user.usage.vas_used.length > 0 ? (
                      <div className="flex flex-wrap gap-1 justify-center">
                        {user.usage.vas_used.map((va) => (
                          <span
                            key={va}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800"
                          >
                            {va}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={9}
                  className="px-6 py-4 text-center text-sm text-gray-500"
                >
                  No monthly token usage data available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  </>
)}
      </DisclosurePanel>
    </Disclosure>
  );
};

export default MonthlyTokenUsageTable;